// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { supabase } from "../lib/supabase"
import { CheckCircle, ArrowRight, Building2, Globe, User, Lock, Mail, Briefcase, Eye, EyeOff } from "lucide-react"
import { GuardRokIcon } from "../components/GuardRokLogo"
import ChatWidget from "../components/ChatWidget"

const PLANS = [
  { id: "free",         label: "Free",         price: "$0",    seats: 2,    features: ["2 users", "5 policies", "1 AI tool", "30-day logs"] },
  { id: "starter",      label: "Starter",      price: "$49",   seats: 25,   features: ["25 users", "20 policies", "2 AI tools", "90-day logs", "Super admin"] },
  { id: "professional", label: "Professional", price: "$149",  seats: 100,  features: ["100 users", "Unlimited policies", "All AI tools", "1-year logs", "All compliance packs", "Slack alerts"] },
  { id: "enterprise",   label: "Enterprise",   price: "$499",  seats: 9999, features: ["Unlimited users", "Everything", "7-year logs (SOX)", "SSO/SAML", "Dedicated CSM"] },
]

const STEPS = ["Company", "Admin Account", "Plan", "Done"]

export default function Signup() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  // Form data
  const [company, setCompany] = useState({ name: "", domain: "", industry: "" })
  const [admin, setAdmin] = useState({ fullName: "", email: "", password: "", confirmPassword: "" })
  const [plan, setPlan] = useState("starter")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  // Pre-select plan from ?plan= query param (e.g. from Pricing page before Stripe is live)
  useEffect(() => {
    const p = searchParams.get("plan")
    if (p && PLANS.find(x => x.id === p)) setPlan(p)
  }, [])

  const INDUSTRIES = [
    "Healthcare",
    "Banking & Finance",
    "Education",
    "Legal",
    "Government",
    "Real Estate",
    "Retail & E-Commerce",
    "Technology",
    "Manufacturing",
    "Insurance",
    "HR & People Ops",
    "Energy & Utilities",
    "Pharmaceutical & Biotech",
    "Telecommunications",
    "Cryptocurrency & Fintech",
    "Accounting & Audit",
    "APAC / International",
    "Media & Entertainment",
    "Consulting",
    "Other",
  ]

  async function handleCompanyNext() {
    setError("")
    if (!company.name.trim()) return setError("Company name is required")
    if (!company.domain.trim()) return setError("Company domain is required")
    if (!company.domain.includes(".")) return setError("Enter a valid domain (e.g. company.com)")
    if (!company.industry) return setError("Please select your industry")

    // Check if domain already registered → send them to sign in instead
    const { data } = await supabase.from("organizations").select("id").eq("domain", company.domain.toLowerCase()).single()
    if (data) { navigate("/login?hint=" + encodeURIComponent(company.domain)); return }

    setStep(1)
  }

  async function handleAdminNext() {
    setError("")
    if (!admin.fullName.trim()) return setError("Full name is required")
    if (!admin.email.trim()) return setError("Email is required")
    if (!admin.email.includes("@")) return setError("Enter a valid email address")
    if (!admin.email.toLowerCase().endsWith("@" + company.domain.toLowerCase())) {
      return setError(`Email must match your company domain (@${company.domain})`)
    }
    if (admin.password.length < 8) return setError("Password must be at least 8 characters")
    if (admin.password !== admin.confirmPassword) return setError("Passwords do not match")
    setStep(2)
  }

  async function handleSignup() {
    setError("")
    setLoading(true)

    try {
      // 1. Create auth user. Pass company info as metadata so the
      //    `handle_new_user` trigger (migration 08) can atomically
      //    create the org + public.users row + default policies.
      //    The RPC below is kept as belt-and-suspenders for orgs
      //    that need plan/seats info the trigger doesn't have.
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: admin.email,
        password: admin.password,
        options: {
          data: {
            full_name:       admin.fullName,
            company_name:    company.name.trim(),
            company_domain:  company.domain.toLowerCase().trim(),
            industry:        company.industry,
          }
        }
      })
      if (authError) throw authError
      if (!authData.user) throw new Error("Failed to create account")

      const userId = authData.user.id
      const selectedPlan = PLANS.find(p => p.id === plan)

      // 2. Upgrade plan/seats via RPC (the trigger created a free-tier org).
      //    If the trigger already created the org with the correct domain,
      //    the RPC's "Domain already registered" branch will return early —
      //    that's fine, the user is correctly attached.
      //    If RPC fails for any reason, swallow and continue: the trigger
      //    has already given the user a working free-tier org.
      const { data: rpcResult } = await supabase.rpc("create_org_and_user", {
        p_org_name: company.name.trim(),
        p_org_domain: company.domain.toLowerCase().trim(),
        p_org_plan: plan,
        p_org_seats: selectedPlan?.seats ?? 5,
        p_org_settings: {
          retention_days: plan === "enterprise" ? 2555 : plan === "professional" ? 365 : plan === "starter" ? 90 : 30,
          allowed_tools: ["chatgpt", "claude", "copilot", "gemini"],
          notification_emails: [admin.email],
          risk_score_alert_threshold: 75,
          require_justification_on_warn: true,
          gdpr_region: "US",
          industry: company.industry,
        },
        p_user_id: userId,
        p_user_full_name: admin.fullName.trim(),
        p_user_email: admin.email,
      })
      // rpcResult may contain { error: "Domain already registered" } if the
      // auth-trigger beat us to it — that's the desired outcome, NOT an error.

      // 3. Default policies are installed automatically by the
      //    `trg_organizations_install_defaults` trigger (migration 08).
      //    This call is now optional — only fires for orgs that already
      //    existed before the trigger was added. Safe to leave as-is.
      const orgId = (rpcResult?.org_id as string) || ""
      if (orgId) {
        try { await installDefaultPolicies(orgId, userId) } catch { /* trigger handled it */ }
      }

      setStep(3)

      // Fire MAYA step-1 welcome email — non-blocking, silent if Anthropic key unfunded
      supabase.functions.invoke("maya", {
        body: {
          org_id:   orgId,
          step:     1,
          org_email: admin.email,
          org_name:  company.name.trim(),
          industry:  company.industry,
          send_to:   admin.email,
        },
      }).catch(e => console.warn("[maya step-1]:", e))

    } catch (err: any) {
      setError(err.message || "Failed to create account. Please try again.")
    }
    setLoading(false)
  }

  async function installDefaultPolicies(orgId: string, userId: string) {
    // Always install SSN and Credit Card policies for every new org
    const defaultPolicies = [
      {
        name: "Block SSN / Tax ID",
        description: "Prevents employees from sharing Social Security Numbers or Tax IDs with AI tools.",
        action: "block", priority: 100, is_active: true,
        conditions: { match: "any", rules: [{ detection_type: "ssn" }] },
        applies_to_roles: ["admin", "manager", "user"],
        applies_to_tools: ["chatgpt", "claude", "copilot", "gemini"],
        rules: [
          { name: "SSN Pattern", type: "ssn", pattern: "\\d{3}-\\d{2}-\\d{4}", severity: "critical" },
          { name: "SSN Keywords", type: "custom_keyword", pattern: "\\b(social security number|ssn|tax id|taxpayer id|itin)\\b", severity: "medium" },
        ]
      },
      {
        name: "Redact Credit Card Numbers",
        description: "Automatically redacts credit card PANs from prompts before they reach AI tools.",
        action: "redact", priority: 85, is_active: true,
        conditions: { match: "any", rules: [{ detection_type: "credit_card" }] },
        applies_to_roles: ["admin", "manager", "user"],
        applies_to_tools: ["chatgpt", "claude", "copilot", "gemini"],
        rules: [
          { name: "Visa Card", type: "credit_card", pattern: "(?<!\\d)4[0-9]{3}[\\s\\-]?[0-9]{4}[\\s\\-]?[0-9]{4}[\\s\\-]?[0-9]{4}(?!\\d)", severity: "critical" },
          { name: "Mastercard", type: "credit_card", pattern: "(?<!\\d)(5[1-5][0-9]{2}|2[2-7][0-9]{2})[\\s\\-]?[0-9]{4}[\\s\\-]?[0-9]{4}[\\s\\-]?[0-9]{4}(?!\\d)", severity: "critical" },
        ]
      },
      {
        name: "Block API Key Leaking",
        description: "Detects and blocks prompts containing API keys or secrets.",
        action: "block", priority: 95, is_active: true,
        conditions: { match: "any", rules: [{ detection_type: "api_key" }] },
        applies_to_roles: ["admin", "manager", "user"],
        applies_to_tools: ["chatgpt", "claude", "copilot", "gemini"],
        rules: [
          { name: "OpenAI / Anthropic Key", type: "api_key", pattern: "sk-[a-zA-Z0-9\\-_]{20,}", severity: "critical" },
          { name: "AWS Access Key", type: "api_key", pattern: "(AKIA|AGPA|AIPA|ANPA|ANVA|ASIA)[A-Z0-9]{16}", severity: "critical" },
        ]
      },
    ]

    for (const policy of defaultPolicies) {
      const { rules, ...policyData } = policy
      const { data: p, error: e } = await supabase.from("policies").insert({
        ...policyData,
        org_id: orgId,
        created_by: userId,
        applies_to_departments: null,
      }).select().single()

      if (e || !p) {
        console.error("Policy install failed:", policy.name, e)
        continue
      }
      const { error: ruleErr } = await supabase.from("detection_rules").insert(
        rules.map(r => ({ ...r, org_id: orgId, policy_id: p.id, is_active: true }))
      )
      if (ruleErr) console.error("Rule install failed for policy", policy.name, ruleErr)
    }
  }

  function passwordStrength(pw: string) {
    let score = 0
    if (pw.length >= 8)           score++
    if (pw.length >= 12)          score++
    if (/[A-Z]/.test(pw))         score++
    if (/[0-9]/.test(pw))         score++
    if (/[^A-Za-z0-9]/.test(pw))  score++
    if (score <= 1) return { score, label: "Weak",   color: "#ef4444" }
    if (score <= 3) return { score, label: "Medium", color: "#f59e0b" }
    return                 { score, label: "Strong", color: "#22c55e" }
  }

  function generatePassword() {
    const up = "ABCDEFGHJKMNPQRSTUVWXYZ", lo = "abcdefghjkmnpqrstuvwxyz"
    const di = "23456789", sp = "!@#$%^&*"
    const all = up + lo + di + sp
    const pw = [up, lo, di, sp].map(s => s[Math.floor(Math.random() * s.length)])
    for (let i = 4; i < 14; i++) pw.push(all[Math.floor(Math.random() * all.length)])
    return pw.sort(() => Math.random() - 0.5).join("")
  }

  const inputClass = "w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/20 transition-all placeholder-gray-600"
  const labelClass = "block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2"

  return (
    <div style={{ minHeight: "100vh", background: "#030712" }} className="flex">

      {/* Left Panel — Branding */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] flex-shrink-0 p-12" style={{ background: "#0a0f1e", borderRight: "1px solid #1f2937" }}>
        <div>
          <div className="flex items-center gap-3 mb-16">
            <GuardRokIcon size={40} />
            <div>
              <div className="text-white font-bold text-lg leading-none">GuardRok™</div>
              <div className="text-gray-500 text-xs uppercase tracking-widest mt-0.5">AI Firewall</div>
            </div>
          </div>

          <div className="mb-12">
            <h1 className="text-3xl font-bold text-white leading-tight mb-4">
              Protect your team from AI data leaks
            </h1>
            <p className="text-gray-400 text-sm leading-relaxed">
              GuardRok intercepts sensitive data before it reaches ChatGPT, Claude, Copilot, and Gemini. Setup takes 5 minutes.
            </p>
          </div>

          <div className="space-y-4">
            {[
              "Works on ChatGPT, Claude, Copilot & Gemini",
              "HIPAA, SOX, GDPR, PCI-DSS compliance packs",
              "Real-time dashboard and violation alerts",
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-3 h-3 text-red-400" />
                </div>
                <span className="text-gray-300 text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-gray-800 rounded-xl p-4">
          <div className="text-gray-500 text-xs mb-2">Trusted for compliance under</div>
          <div className="flex flex-wrap gap-2">
            {["HIPAA", "SOX", "GDPR", "PCI-DSS", "FERPA", "FINRA"].map(l => (
              <span key={l} className="text-xs bg-gray-800 text-gray-400 px-2 py-1 rounded-md font-mono">{l}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <GuardRokIcon size={30} />
            <span className="text-white font-bold">GuardRok™</span>
          </div>

          {/* Progress steps */}
          {step < 3 && (
            <div className="flex items-center gap-2 mb-8">
              {STEPS.slice(0, 3).map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    i < step ? "bg-red-500 text-white" :
                    i === step ? "bg-red-500 text-white ring-4 ring-red-500/20" :
                    "bg-gray-800 text-gray-500"
                  }`}>
                    {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-xs font-medium ${i === step ? "text-white" : "text-gray-600"}`}>{s}</span>
                  {i < 2 && <div className={`w-8 h-px ${i < step ? "bg-red-500" : "bg-gray-800"}`} />}
                </div>
              ))}
            </div>
          )}

          {/* ── STEP 0: Company ── */}
          {step === 0 && (
            <div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-1">Set up your company</h2>
                <p className="text-gray-500 text-sm">We'll create a GuardRok workspace for your organization.</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>
                    <Building2 className="w-3 h-3 inline mr-1" />Company Name
                  </label>
                  <input value={company.name} onChange={e => setCompany(p => ({ ...p, name: e.target.value }))}
                    placeholder="Acme Corporation" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>
                    <Globe className="w-3 h-3 inline mr-1" />Company Domain
                  </label>
                  <input value={company.domain} onChange={e => setCompany(p => ({ ...p, domain: e.target.value.toLowerCase() }))}
                    placeholder="acme.com" className={inputClass} />
                  <p className="text-gray-600 text-xs mt-1.5">All employees with @{company.domain || "yourcompany.com"} email will auto-join</p>
                </div>
                <div>
                  <label className={labelClass}>
                    <Briefcase className="w-3 h-3 inline mr-1" />Industry
                  </label>
                  <select value={company.industry} onChange={e => setCompany(p => ({ ...p, industry: e.target.value }))}
                    className={inputClass + " appearance-none"} style={{ background: "#111827" }}>
                    <option value="">Select your industry</option>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                {error && <div className="bg-red-950 border border-red-800 text-red-400 text-sm rounded-xl px-4 py-3">{error}</div>}
                <button onClick={handleCompanyNext}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 1: Admin Account ── */}
          {step === 1 && (
            <div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-1">Create your admin account</h2>
                <p className="text-gray-500 text-sm">You'll be the super admin for <span className="text-white font-medium">{company.name}</span>.</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}><User className="w-3 h-3 inline mr-1" />Full Name</label>
                  <input value={admin.fullName} onChange={e => setAdmin(p => ({ ...p, fullName: e.target.value }))}
                    placeholder="Jane Smith" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}><Mail className="w-3 h-3 inline mr-1" />Work Email</label>
                  <input type="email" value={admin.email} onChange={e => setAdmin(p => ({ ...p, email: e.target.value }))}
                    placeholder={`you@${company.domain}`} className={inputClass} />
                  <p className="text-gray-600 text-xs mt-1.5">Must be a @{company.domain} email address</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelClass} style={{marginBottom:0}}><Lock className="w-3 h-3 inline mr-1" />Password</label>
                    <button type="button" onClick={() => { const pw = generatePassword(); setAdmin(p => ({ ...p, password: pw, confirmPassword: pw })) }}
                      style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:"#e63946",padding:0,fontWeight:600}}>
                      ✦ Suggest password
                    </button>
                  </div>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} value={admin.password} onChange={e => setAdmin(p => ({ ...p, password: e.target.value }))}
                      placeholder="Min 8 characters" className={inputClass + " pr-10"} />
                    <button type="button" onClick={() => setShowPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {admin.password && (() => {
                    const s = passwordStrength(admin.password)
                    return (
                      <div style={{marginTop:8}}>
                        <div style={{height:4,background:"#1f2937",borderRadius:4,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${(s.score/5)*100}%`,background:s.color,borderRadius:4,transition:"all 0.3s"}} />
                        </div>
                        <div style={{fontSize:11,color:s.color,marginTop:4,fontWeight:600}}>{s.label} password</div>
                      </div>
                    )
                  })()}
                </div>
                <div>
                  <label className={labelClass}><Lock className="w-3 h-3 inline mr-1" />Confirm Password</label>
                  <div className="relative">
                    <input type={showConfirm ? "text" : "password"} value={admin.confirmPassword} onChange={e => setAdmin(p => ({ ...p, confirmPassword: e.target.value }))}
                      placeholder="Repeat password" className={inputClass + " pr-10"} />
                    <button type="button" onClick={() => setShowConfirm(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {error && <div className="bg-red-950 border border-red-800 text-red-400 text-sm rounded-xl px-4 py-3">{error}</div>}
                <button onClick={handleAdminNext}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => { setStep(0); setError("") }}
                  className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 transition-colors">
                  ← Back
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 2: Plan ── */}
          {step === 2 && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white mb-1">Choose your plan</h2>
                <p className="text-gray-500 text-sm">Start free, upgrade anytime. No credit card required for Free.</p>
              </div>
              <div className="space-y-3 mb-6">
                {PLANS.map(p => (
                  <div key={p.id} onClick={() => setPlan(p.id)}
                    className={`border rounded-xl p-4 cursor-pointer transition-all ${
                      plan === p.id
                        ? "border-red-500 bg-red-500/10"
                        : "border-gray-800 hover:border-gray-700 bg-gray-900/50"
                    }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          plan === p.id ? "border-red-500" : "border-gray-600"
                        }`}>
                          {plan === p.id && <div className="w-2 h-2 rounded-full bg-red-500" />}
                        </div>
                        <span className="text-white font-semibold text-sm">{p.label}</span>
                        {p.id === "starter" && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-medium">Popular</span>}
                      </div>
                      <div className="text-right">
                        <span className="text-white font-bold">{p.price}</span>
                        <span className="text-gray-500 text-xs">/mo</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 ml-7">
                      {p.features.map((f, i) => (
                        <span key={i} className="text-xs text-gray-400">{i > 0 ? "·" : ""} {f}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {error && <div className="bg-red-950 border border-red-800 text-red-400 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}
              <button onClick={handleSignup} disabled={loading}
                className="w-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2">
                {loading ? "Creating your workspace..." : "Create GuardRok Workspace"}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
              <button onClick={() => { setStep(1); setError("") }}
                className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 mt-2 transition-colors">
                ← Back
              </button>
              <p className="text-center text-gray-600 text-xs mt-4">
                By signing up you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          )}

          {/* ── STEP 3: Success ── */}
          {step === 3 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">You're all set! 🎉</h2>
              <p className="text-gray-400 text-sm mb-8 leading-relaxed">
                Your GuardRok workspace for <span className="text-white font-medium">{company.name}</span> is ready.
                3 default policies have been installed automatically.
              </p>

              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6 text-left space-y-3">
                <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Next Steps</div>
                {[
                  { n: "1", t: "Sign into your dashboard", d: "View real-time prompt monitoring", href: null },
                  { n: "2", t: "Install Chrome extension", d: "Add GuardRok to your browser", href: "https://chromewebstore.google.com/detail/guardrok-ai-firewall/dldblbcpofdflbbhpahbokamikpakhdh" },
                  { n: "3", t: "Invite your team", d: "They auto-join when signing up with @" + company.domain, href: null },
                  { n: "4", t: "Install compliance packs", d: "HIPAA, SOX, GDPR and more in Compliance Hub", href: null },
                ].map(({ n, t, d, href }) => (
                  <div key={n} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-red-400 text-xs font-bold">{n}</span>
                    </div>
                    <div>
                      {href ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="text-white text-sm font-medium underline decoration-red-500/50 hover:decoration-red-400">{t} →</a>
                      ) : (
                        <div className="text-white text-sm font-medium">{t}</div>
                      )}
                      <div className="text-gray-500 text-xs">{d}</div>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={() => navigate("/")}
                className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2">
                Go to Dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

        </div>
      </div>
      <ChatWidget />
    </div>
  )
}
